/**
 * Settings sections — the rail's source of truth.
 *
 * Per-item `permission` gates the section behind a `settings.manage` /
 * `team.manage` / … string from `packages/api-contract/src/permissions.ts`.
 * The rail renders items without permission as disabled (same visual as the
 * "Coming soon" stubs) so a non-Admin operator sees the surface but can't
 * click through. Server-side is the actual floor for anything sensitive;
 * this is UX defense-in-depth. Items without a `permission` field are
 * visible + clickable for every seated member — currently Profile and
 * About & status, which are personal / informational.
 *
 * Mirrors `prototype/settings/settings-company.html ▸ .set-rail` exactly: 5 groups,
 * 16 items. Each item knows whether it's `built` (real route) or a stub
 * (rail item is disabled with a "Coming soon" tooltip — same V1.x pattern
 * as the workspace sidebar).
 *
 * Per-item `pip` is the small status chip on the right (e.g. `2FA`,
 * `Trial`, `DKIM`). Pip tone follows the prototype's `info|warn|issue`
 * mapping. We render the static set today; later when each section ships
 * with real data, the pip becomes dynamic.
 *
 * Adding a new section:
 *   1. Add the item to a group below.
 *   2. Set `built: true` and add the route at `/settings/<id>`.
 *   3. Update this file's tests if you add a sixth group.
 */
import type { Permission } from "@flint/api-contract";

export type SectionId =
  | "profile"
  | "subscription"
  | "billing"
  | "company"
  | "gstin"
  | "tax"
  | "locale"
  | "numbering"
  | "recurring"
  | "templates"
  | "communication"
  | "rules"
  | "payments"
  | "catalogue"
  | "team"
  | "roles"
  | "approvals"
  | "notifications"
  | "assistant"
  | "security"
  | "about";

export type PipTone = "info" | "warn" | "issue";

export interface SettingsSection {
  id: SectionId;
  label: string;
  /** Optional small chip on the right (e.g. "2FA", "Trial"). */
  pip?: { text: string; tone: PipTone };
  /** When false, rail item renders disabled with a "Coming soon" tooltip. */
  built: boolean;
  /**
   * Hide the rail row entirely. Used while we tighten the V1.0 surface —
   * sections whose underlying behaviour ships in V1.x can stay defined
   * here (so the rail flips back trivially later) without confusing the
   * V1.0 operator with a row they can't use. Empty groups are also hidden.
   */
  hidden?: boolean;
  /**
   * Module entitlement (`company_entitlements.modules`) this section is gated
   * by — e.g. the Flint AI page requires the `ai.assistant` module (the AI
   * add-on). The rail hides the row when the company lacks the module.
   */
  module?: string;
  /**
   * Permission (`roles.permissions[]`) required to click into this section.
   * When set, the rail renders the row as a disabled button (visible but
   * not clickable) for any user who lacks it. Omit for sections open to
   * every seated member (Profile, About).
   */
  permission?: Permission;
}

export interface SettingsGroup {
  /** Group caption shown above the items. */
  label: string;
  items: ReadonlyArray<SettingsSection>;
}

/**
 * The 5-group rail. Order + labels match the prototype. Today only
 * `company` is built; the rest are honest V1.x stubs.
 */
export const SETTINGS_GROUPS: ReadonlyArray<SettingsGroup> = [
  {
    // Account — first group in the rail. Profile is the only V1.0-built
    // item; the rest (Subscription, Integrations, Security) are V1.x
    // stubs that stay visible as disabled rows so the operator sees the
    // roadmap. Moving Profile inside settings (rather than the old
    // top-level `/profile`) keeps every self-serve setting in one place.
    label: "Account",
    items: [
      { id: "profile", label: "Profile", built: true },
      // Subscription — the plan, usage, add-ons, and self-serve activation /
      // upgrade. Billing history (invoices + payment method) is the separate
      // "Billing" row below.
      // The pip is NOT static — it reflects the live subscription status
      // (Trial / Activate / Past due / …) and disappears when active. Computed
      // in settings-rail.tsx from the session's `company.subscription.status`.
      {
        id: "subscription",
        label: "Subscription",
        built: true,
        permission: "billing.manage",
      },
      // Billing — invoices the customer can download + the payment method on
      // file. Invoices are Razorpay's (merchant of record); empty until the
      // first charge.
      { id: "billing", label: "Billing", built: true, permission: "billing.manage" },
      // Flint AI — configure the assistant: default language, channels
      // (WhatsApp/Telegram/Slack), capabilities, and AI spend. Gated by the AI
      // add-on (migration 0107): the row hides when Flint AI is off; turn it
      // back on from Subscription → Add-ons.
      { id: "assistant", label: "Flint AI", built: true, module: "ai.assistant", permission: "settings.manage" },
      // Security & data — 2FA / sessions / data export wire onto
      // identity.recovery + identity.api-keys which are V1.x stubs.
      {
        id: "security",
        label: "Security & data",
        built: false,
        pip: { text: "2FA", tone: "issue" },
        hidden: true,
      },
    ],
  },
  {
    label: "Business identity",
    items: [
      { id: "company", label: "Company profile", built: true, permission: "settings.manage" },
      { id: "gstin", label: "GSTIN & branches", built: true, permission: "settings.manage" },
      // Tax & compliance — entire surface (GSTR cadence, TDS toggles, TCS,
      // HSN defaults, LUT tracking) depends on integrations that ship in
      // V1.x. Hidden from the rail until at least one card has real
      // backing logic; the section stays defined so the rail flips back
      // by removing `hidden: true`.
      {
        id: "tax",
        label: "Tax & compliance",
        built: false,
        pip: { text: "5d", tone: "issue" },
        hidden: true,
      },
      // Localisation — V1.0 is English-only / INR-locked / IST-only /
      // Indian-number-grouping by default (AGENTS.md §14 + system-decisions
      // §6). The language picker + tz/format selectors have nothing to
      // configure today. Flip back when translation bundles ship.
      { id: "locale", label: "Localisation", built: false, hidden: true },
    ],
  },
  {
    label: "Sales & documents",
    items: [
      // Numbering — V1.0 editor for prefix + padding + first-issue
      // starting-number per V1.0 doc type. Backs onto `document_number_pool`
      // + `allocate-doc-number.ts`. Pads inherit on FY rollover so the
      // operator's choices carry forward without touching this page in
      // April.
      { id: "numbering", label: "Numbering", built: true, permission: "settings.manage" },
      // Recurring documents — auto-draft an invoice or vendor bill on a
      // calendar cadence (ADR-0024 / ADR-0025). Backs onto
      // `recurring_schedules` + the daily `recurring-schedules-tick` cron.
      // V1 generates drafts only.
      { id: "recurring", label: "Recurring documents", built: true, permission: "settings.manage" },
      // Document templates — the print-template manager (ADR-0021). Create /
      // duplicate / edit HTML templates, preview them, and pick which is the
      // active default per document kind (invoice, bill, …). Flint's built-in
      // defaults are read-only; operators duplicate-to-customise.
      { id: "templates", label: "Document templates", built: true, permission: "settings.manage" },
      // Communication — WhatsApp Business + Email DKIM + SMS + reminder
      // cadences + auto-actions all ride on `notifications/adapters/*`
      // (Index-only) and `automations/` (Index-only). The DKIM pip is
      // decorative until the email-domain verification UI lands.
      {
        id: "communication",
        label: "Communication",
        built: false,
        pip: { text: "DKIM", tone: "warn" },
        hidden: true,
      },
      // Customer & vendor rules — credit-term defaults, trust-window
      // thresholds, tier definitions, vendor defaults. The PoS default
      // already lives on /settings/gstin; the rest are V1.x automations.
      { id: "rules", label: "Customer & vendor rules", built: false, hidden: true },
    ],
  },
  {
    label: "Cash & catalogue",
    items: [
      // Payments & UPI — UPI on invoice already lives on /settings/company
      // (bank-details card). Payment gateways + auto-recon settings are
      // V1.x (system-decisions principle 4: we never hold customer money).
      { id: "payments", label: "Payments & UPI", built: false, hidden: true },
      // Catalogue defaults — inventory tracking, units of measure, stock
      // locations, pricing strategy. None of the four cards have real
      // backing — items already carry per-row HSN/unit/rate.
      { id: "catalogue", label: "Catalogue defaults", built: false, hidden: true },
    ],
  },
  {
    label: "People & control",
    items: [
      { id: "team", label: "Team & members", built: true, permission: "team.manage" },
      // Roles & org (ADR-0030) — the dynamic role editor (permission matrix) +
      // the department tree. Supersedes the read-only capability matrix.
      { id: "roles", label: "Roles & org", built: true, permission: "roles.manage" },
      // Approvals (ADR-0031) — the configurable approval engine: a reusable
      // chain library + per-module conditional bindings, with a live preview.
      { id: "approvals", label: "Approvals", built: true, permission: "approvals.manage" },
      // Notifications — the master kill switch (Settings → Notifications,
      // migration 0088). V1.0 ships the single tenant-wide on/off toggle;
      // per-event / per-channel preferences land in V1.x.
      { id: "notifications", label: "Notifications", built: true, permission: "settings.manage" },
    ],
  },
  {
    // System — app metadata + live service status (Settings → About).
    // Visible to every member; the page itself gates nothing.
    label: "System",
    items: [{ id: "about", label: "About & status", built: true }],
  },
];

/** Lookup helper for breadcrumbs / page heads. */
export function findSection(id: SectionId): SettingsSection | null {
  for (const group of SETTINGS_GROUPS) {
    for (const item of group.items) {
      if (item.id === id) return item;
    }
  }
  return null;
}
